import React from 'react';
import { CalendarDays, MapPin, Users, Hash, ShieldCheck, AlertTriangle, UserCog } from 'lucide-react';
import { Badge } from '../ui';
import { fixMojibake } from '../../lib/utils';
import { nombreIncluyeRama } from '../../lib/division';

// La convocatoria del torneo, para LEERLA antes de aceptar.
//
// Antes el club aceptaba a ciegas: el correo decía "te invitan a MYKLUBB LAGUNA" y ya.
// Las categorías las descubría después, cuando ya había aceptado y iba a inscribir --
// y si no habían convocado la suya, aceptó para nada.
//
// Lo que se muestra sale de get_convocatoria, y el rango de cada categoría es el que
// el torneo CONGELÓ al convocar, no el que tenga hoy el catálogo del organizador. Es
// la regla real contra la que se va a validar su roster.

const fecha = (v) => (v
  ? new Date(`${v}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
  : null);

const rango = (cat) => {
  const d = cat.fecha_nac_desde ? new Date(`${cat.fecha_nac_desde}T12:00:00`) : null;
  const h = cat.fecha_nac_hasta ? new Date(`${cat.fecha_nac_hasta}T12:00:00`) : null;
  // El año solo, salvo que el corte caiga a media temporada: ahí el día importa y
  // esconderlo haría que un club creyera que todo el año entra.
  const aBorde = (x, mes, dia) => x && x.getMonth() === mes && x.getDate() === dia;
  const limpio = aBorde(d, 0, 1) && aBorde(h, 11, 31);
  if (d && h) {
    return limpio
      ? (d.getFullYear() === h.getFullYear()
          ? `Nacidos en ${d.getFullYear()}`
          : `Nacidos ${d.getFullYear()}-${h.getFullYear()}`)
      : `Nacidos del ${fecha(cat.fecha_nac_desde)} al ${fecha(cat.fecha_nac_hasta)}`;
  }
  if (d) return `Nacidos desde ${limpio ? d.getFullYear() : fecha(cat.fecha_nac_desde)}`;
  if (h) return `Nacidos hasta ${limpio ? h.getFullYear() : fecha(cat.fecha_nac_hasta)}`;
  return 'Sin límite de edad';
};

const Dato = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <span>{children}</span>
  </div>
);

const Convocatoria = ({ convocatoria, className = '' }) => {
  if (!convocatoria) return null;
  const c = convocatoria;
  const cats = c.categorias || [];

  return (
    <div className={`space-y-5 text-left ${className}`}>
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Convocatoria</p>
        <h2 className="text-xl font-bold leading-tight">{fixMojibake(c.nombre || 'Torneo')}</h2>
        {c.organizador && (
          <p className="text-sm text-muted-foreground">Organiza {fixMojibake(c.organizador)}</p>
        )}
        {c.descripcion && (
          <p className="pt-1 text-sm text-muted-foreground">{fixMojibake(c.descripcion)}</p>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {(c.fecha_inicio || c.fecha_fin) && (
          <Dato icon={CalendarDays}>
            {c.fecha_inicio && c.fecha_fin
              ? <>Del {fecha(c.fecha_inicio)} al {fecha(c.fecha_fin)}</>
              : <>{fecha(c.fecha_inicio || c.fecha_fin)}</>}
          </Dato>
        )}
        {(c.sede || c.ubicacion) && (
          <Dato icon={MapPin}>{fixMojibake(c.sede || c.ubicacion)}</Dato>
        )}
        {c.max_jugadores_grupo && (
          <Dato icon={Users}>Hasta {c.max_jugadores_grupo} jugadores por equipo</Dato>
        )}
        {c.max_staff_grupo && (
          <Dato icon={UserCog}>Hasta {c.max_staff_grupo} staff por equipo</Dato>
        )}
        {c.dorsal_unico && <Dato icon={Hash}>Dorsal único por equipo</Dato>}
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold">
          Categorías convocadas {cats.length > 0 && <span className="text-muted-foreground">({cats.length})</span>}
        </p>

        {cats.length === 0 ? (
          // Vale la pena decirlo fuerte: aceptar una convocatoria sin categorías no
          // sirve de nada, y es mejor que el club lo sepa antes.
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Todavía no hay categorías convocadas. Pregúntale a la organizadora antes de
              inscribir equipos.
            </span>
          </div>
        ) : (
          <ul className="divide-y rounded-lg border">
            {cats.map((cat) => (
              <li key={cat.convocatoria_id || cat.categoria_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{fixMojibake(cat.nombre)}</p>
                  <p className="text-xs text-muted-foreground">
                    {rango(cat)}
                    {cat.peso_max_kg ? ` · Peso máx ${cat.peso_max_kg} kg` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {cat.rama && !nombreIncluyeRama(cat.nombre, cat.rama) && (
                    <Badge variant="outline" className="text-[10px]">{cat.rama}</Badge>
                  )}
                  {/* Que el club sepa de antemano si la edad va a bloquearlo o solo a
                      avisarle: cambia lo que puede llevar al torneo. */}
                  {cat.tipo_restriccion === 'estricta' && (
                    <Badge variant="outline" className="gap-1 text-[10px]">
                      <ShieldCheck className="h-3 w-3" /> Edad estricta
                    </Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(c.fecha_limite_roster || c.max_grupos_por_club || c.max_grupos_por_club_categoria) && (
        <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
          <p className="mb-1 font-medium text-foreground">Antes de inscribir</p>
          <ul className="space-y-0.5">
            {c.fecha_limite_roster && (
              <li>El roster se cierra el <strong className="text-foreground">{fecha(c.fecha_limite_roster)}</strong>. Después, cada cambio lo aprueba la organizadora.</li>
            )}
            {c.max_grupos_por_club && <li>Máximo {c.max_grupos_por_club} equipos por club.</li>}
            {c.max_grupos_por_club_categoria && (
              <li>Máximo {c.max_grupos_por_club_categoria} por división.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Convocatoria;
